import { newUuid } from './utils/uuid.js';
// Product budget: a comment may carry up to ten files, totaling 10 MiB.
export const COMMENT_ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;
export const COMMENT_ATTACHMENT_MAX_COUNT = 10;
// Base64 expands bytes by 4/3. Reserve room for filenames and comment text.
export const COMMENT_ATTACHMENT_BODY_BYTES = /* @__PURE__ */ Math.ceil(COMMENT_ATTACHMENT_MAX_BYTES * 4 / 3) + 64 * 1024;
export const COMMENT_ATTACHMENT_ACCEPT = '.png,.jpg,.jpeg,.pdf';
export const COMMENT_ATTACHMENT_HINT = 'PNG, JPG or PDF. Up to 10 MB total.';
/**
 * The one sentence every surface shows when a comment file did not attach: team review and the extension through
 * popoverFileQueue, the guest reply through section 0200. The guest's wording, kept (Emily, 2026-10-25, decision 6):
 * the team popover used to render the client's raw "Request failed (400)" where the guest showed this.
 */
export const COMMENT_ATTACH_FAILED_COPY = "Files couldn't be attached. Please try again.";
/** What the file picker accepts. Video arrives only from the Record tool (Approach B, picking a video file, is parked). */
export const COMMENT_PICKER_TYPES = ['image/png', 'image/jpeg', 'application/pdf'];
/** What Chrome's MediaRecorder produces; the server accepts these only on routes that opt in. */
export const COMMENT_VIDEO_TYPES = ['video/mp4', 'video/webm'];
export function isVideoAttachment(contentType) {
    return COMMENT_VIDEO_TYPES.includes(contentType);
}
export function attachmentType(filename) {
    const ext = filename.toLowerCase().split('.').pop();
    return ext === 'png' ? 'image/png' : ext === 'jpg' || ext === 'jpeg' ? 'image/jpeg' : ext === 'pdf' ? 'application/pdf'
        : ext === 'mp4' ? 'video/mp4' : ext === 'webm' ? 'video/webm' : null;
}
export async function readCommentFiles(files, existing = []) {
    if (files.length + existing.length > COMMENT_ATTACHMENT_MAX_COUNT)
        throw new Error('Attach up to 10 files per comment.');
    if (files.reduce((sum, file) => sum + file.size, existing.reduce((sum, file) => sum + file.size, 0)) > COMMENT_ATTACHMENT_MAX_BYTES) {
        throw new Error('Files exceed the 10 MB limit. Remove a file or choose a smaller one.');
    }
    return Promise.all(files.map(async (file) => {
        const contentType = attachmentType(file.name);
        if (!contentType || !COMMENT_PICKER_TYPES.includes(contentType))
            throw new Error('Choose PNG, JPG or PDF files.');
        if (!file.size)
            throw new Error('This file is empty. Choose a different file.');
        const base64 = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(String(reader.result).split(',')[1]);
            reader.onerror = () => reject(new Error('Could not read this file. Please choose it again.'));
            reader.onabort = () => reject(new Error('Reading the file was cancelled.'));
            reader.readAsDataURL(file);
        });
        return { id: newUuid(), filename: file.name, size: file.size, contentType, base64 };
    }));
}
export function downloadCommentFile(file) {
    const bytes = Uint8Array.from(atob(file.base64), char => char.charCodeAt(0));
    // Always download; never execute a PDF or other attachment in the app origin.
    const url = URL.createObjectURL(new Blob([bytes], { type: 'application/octet-stream' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = file.filename;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 0);
}
export function attachmentSizeLabel(bytes) {
    return bytes < 1024 * 1024 ? `${Math.max(1, Math.ceil(bytes / 1024))} KB` : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
//# sourceMappingURL=commentAttachments.js.map